import { useEffect, useState } from 'react';

/**
 * useMediaQuery — small hydration-safe matchMedia hook.
 * Returns `true` when the query matches the current viewport.
 *
 * Pattern is intentionally simple: lazy-evaluate initial state to avoid a
 * first-render flash on the desktop branch when we're actually on mobile
 * (CRA only runs in the browser — no SSR — so `window.matchMedia` is safe
 * to read synchronously), then subscribe to `change` for live updates.
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    // Reset to current value in case query string changed
    setMatches(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

export default useMediaQuery;
